import { CanAccess } from '@modules/common/components/CanAccess';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import ModalHeader from '@modules/common/components/Modal/ModalHeader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { getFeedbackRetrievalTimeEstimate } from '@modules/quantify/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { useState } from 'react';
import useToggle from 'v2/core/hooks/useToggle';
import { useGetUser } from 'v2/core/services/user';
import { AllFeedbackContentsImage } from 'v2/views/components/ExportFeedbackRecordsModalContent/AllFeedbackContentsImage';
import { PredictionsAndMetadataImage } from 'v2/views/components/ExportFeedbackRecordsModalContent/PredictionsAndMetadataImage';

enum FeedbackRecordType {
  PredictionsAndMetadata = 'PredictionsAndMetadata',
  AllFeedbackContents = 'AllFeedbackContents',
}

export function ExportFeedbackRecordsModalContent({
  exportFeedback,
  feedbackCount,
}: {
  exportFeedback: ({
    includeContent,
    includeOriginalContent,
  }: {
    includeContent?: boolean;
    includeOriginalContent?: boolean;
  }) => Promise<void>;
  feedbackCount?: number;
}) {
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();
  const { email } = useGetUser();

  const [selectedFeedbackType, setSelectedFeedbackType] =
    useState<FeedbackRecordType>(FeedbackRecordType.PredictionsAndMetadata);
  const [shouldIncludeOriginalContent, setShouldIncludeOriginalContent] =
    useState(false);

  const [isLoading, toggleIsLoading] = useToggle(false);

  const handleClose = () => {
    closeModal();
  };

  const handleFeedbackTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSelectedFeedbackType(event.target.value as FeedbackRecordType);
  };

  const handleSubmit = async () => {
    toggleIsLoading();
    try {
      const shouldIncludeAllContents =
        selectedFeedbackType === FeedbackRecordType.AllFeedbackContents;
      await exportFeedback({
        includeContent: shouldIncludeAllContents,
        includeOriginalContent: shouldIncludeOriginalContent,
      });
    } catch {
      showErrorNotification({
        contentComponent: `Failed to send feedback records`,
        props: {},
      });
    } finally {
      toggleIsLoading();
      closeModal();
    }
  };

  return (
    <>
      <ModalHeader title="Export Feedback" />
      <Divider
        sx={{
          opacity: 0.12,
        }}
      />
      <DialogContent>
        <Box>
          <Typography variant="body1" component="span" sx={{ opacity: 0.7 }}>
            Feedback records will be sent to your email at{' '}
          </Typography>
          <Typography variant="subtitle1" component="span">
            {email}
          </Typography>
        </Box>
        <Box mt={2}>
          <Typography variant="body1" sx={{ opacity: 0.7 }}>
            What would you like to include in your export?
          </Typography>
        </Box>
        <Box mt={2}>
          <RadioGroup
            row
            value={selectedFeedbackType}
            onChange={handleFeedbackTypeChange}
            sx={{
              alignItems: 'flex-start',
              gap: 6,
            }}
          >
            <FormControlLabel
              value={FeedbackRecordType.PredictionsAndMetadata}
              control={<Radio />}
              sx={{
                alignItems: 'flex-start',
                gap: 1,
              }}
              label={
                <Stack spacing={1} mt={1}>
                  <Typography variant="body1">Without raw feedback</Typography>
                  <Box>
                    <PredictionsAndMetadataImage />
                  </Box>
                </Stack>
              }
            />
            <FormControlLabel
              value={FeedbackRecordType.AllFeedbackContents}
              control={<Radio />}
              sx={{
                alignItems: 'flex-start',
                gap: 1,
              }}
              label={
                <Stack spacing={1} mt={1}>
                  <Typography variant="body1">With raw feedback</Typography>
                  <Box>
                    <AllFeedbackContentsImage />
                  </Box>
                </Stack>
              }
            />
          </RadioGroup>
        </Box>
        {selectedFeedbackType === FeedbackRecordType.AllFeedbackContents && (
          <Box mt={3}>
            <FormControlLabel
              label={
                <Typography variant="body1">
                  Include non-English feedback in its original language
                </Typography>
              }
              control={
                <Checkbox
                  checked={shouldIncludeOriginalContent}
                  onChange={(event) =>
                    setShouldIncludeOriginalContent(event.target.checked)
                  }
                />
              }
            />
          </Box>
        )}
        <Stack mt={3} alignItems="center" direction="row" spacing={1}>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
            }}
          />
          <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
            We will send a CSV file of the exported feedback to your email
            within the next {getFeedbackRetrievalTimeEstimate(feedbackCount)}.
          </Typography>
        </Stack>
      </DialogContent>
      <Divider
        sx={{
          opacity: 0.12,
        }}
      />
      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          endIcon={<EscHotKeyIcon />}
        >
          Cancel
        </Button>
        <CanAccess
          resource={PermissionResource.Artefact}
          operation={PermissionOperation.Write}
        >
          <LoadingButton
            autoFocus
            onClick={handleSubmit}
            variant="contained"
            loading={isLoading}
          >
            Export
          </LoadingButton>
        </CanAccess>
      </DialogActions>
    </>
  );
}
